import React, { useState, useEffect, useContext, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import AnalyticsHeader from "./AnalyticsHeader";
import ActivityTab from "./ActivityTab";
import BestTimesTab from "./BestTimesTab";
import NumbersTab from "./NumbersTab";
import CallAnalyticsAccessDenied from "./components/CallAnalyticsAccessDenied";
import Text from "../../Tags/Text";
import SharedDataContext from "../../../SharedDataContext";
import { useSmartQuery } from "../../../apiHooks/useSmartQuery";
import { getMembers } from "../../../apis/workspace/members";
import { Skeleton } from "../../ui/skeleton";
import { useCaptureEvent } from "../../../hooks/useCaptureEvent";

export default function AnalyticsContainer() {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};

  const [activeTab, setActiveTab] = useState("activity");
  const [searchParams, setSearchParams] = useSearchParams();
  const captureEvent = useCaptureEvent();
  const hasTrackedPageViewRef = useRef(false);

  const [paginatedUserData, setPaginatedUserData] = useState({
    data: [],
    pagination: {
      currentPage: 0,
      lastPage: 1,
      perPage: 12,
      total: 0,
    },
  });
  const [isLoadingMoreUsers, setIsLoadingMoreUsers] = useState(false);

  useEffect(() => {
    if (activeWorkspace?.id && !hasTrackedPageViewRef.current) {
      captureEvent("ANL_PageView", {
        workspace_id: activeWorkspace.id,
      });
      hasTrackedPageViewRef.current = true;
    }
  }, [activeWorkspace?.id, captureEvent]);

  // Initial fetch for first page of users
  const {
    data: userAccessData,
    error: accessError,
    isLoading: isCheckingAccess,
  } = useSmartQuery(
    ["workspace_members", activeWorkspace?.id],
    () =>
      getMembers({
        query: "page=1&per_page=12&sort=",
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
            "Content-Type": "application/json",
          },
        },
      }),
    {
      condition: !!activeWorkspace?.id,
      enabled: !!activeWorkspace?.id,

      retry: false,

      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,

      staleTime: Infinity,
      cacheTime: Infinity,
    }
  );

  useEffect(() => {
    if (userAccessData && !isCheckingAccess) {
      setPaginatedUserData({
        data: userAccessData.data || [],
        pagination: userAccessData.pagination || {
          currentPage: 1,
          lastPage: 1,
          perPage: 12,
          total: userAccessData.data?.length || 0,
        },
      });
    }
  }, [userAccessData, isCheckingAccess]);

  const handleLoadMoreUsers = useCallback(
    async (nextPage) => {
      if (isLoadingMoreUsers || !activeWorkspace?.id) {
        return;
      }

      try {
        setIsLoadingMoreUsers(true);

        const response = await getMembers({
          query: `page=${nextPage}&per_page=12&sort=`,
          options: {
            headers: {
              "X-Workspace-Id": activeWorkspace.id,
              "Content-Type": "application/json",
            },
          },
        });

        if (response?.data) {
          setPaginatedUserData((prev) => {
            const updated = {
              data: [...prev.data, ...response.data],
              pagination: response.pagination || prev.pagination,
            };

            return updated;
          });
        }
      } catch (error) {
        console.error("Error loading more users:", error);
      } finally {
        setIsLoadingMoreUsers(false);
        console.log("Finished loading page", nextPage);
      }
    },
    [activeWorkspace?.id, isLoadingMoreUsers]
  );

  const hasAccessDenied =
    accessError &&
    (accessError.status === 403 ||
      accessError.response?.status === 403 ||
      accessError.status === 404 ||
      accessError.response?.status === 404);

  const dateRangeMap = {
    "7d": "7 days",
    "30d": "30 days",
    "60d": "60 days",
    "90d": "90 days",
    "180d": "180 days",
    "1y": "1 year",
    custom: "custom",
  };

  const reverseDateRangeMap = {
    "7 days": "7d",
    "30 days": "30d",
    "60 days": "60d",
    "90 days": "90d",
    "180 days": "180d",
    "1 year": "1y",
    custom: "custom",
  };

  const callDirectionMap = {
    outbound: "Outbound",
    inbound: "Inbound",
    all: "All",
  };

  const reverseCallDirectionMap = {
    Outbound: "outbound",
    Inbound: "inbound",
    All: "all",
  };

  const [filters, setFilters] = useState({
    userTeamSelector: null,
    viewToggle: "Team",
    dateRange: dateRangeMap[searchParams.get("time_range")] || "7 days",
    callDirection: callDirectionMap[searchParams.get("call_direction")] || "Outgoing",
    customStartDate: searchParams.get("start_date") || undefined,
    customEndDate: searchParams.get("end_date") || undefined,
  });

  useEffect(() => {
    const current = Object.fromEntries(searchParams.entries());
    const hasDateRange = current.time_range;

    if (!hasDateRange) {
      const defaultParams = {
        time_range: current.time_range || "7d",
      };

      if (activeTab === "activity") {
        defaultParams.call_direction = current.call_direction || "Outgoing";
      }

      setSearchParams(defaultParams);
    }

    if (current.time_range === "custom" && current.start_date && current.end_date) {
      setFilters((prev) => ({
        ...prev,
        dateRange: "custom",
        customStartDate: current.start_date,
        customEndDate: current.end_date,
      }));
    }
  }, [activeTab]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [key]: value,
      };

      if (key === "viewToggle" && value === "Individuals" && !prev.userTeamSelector) {
        // This will be handled by AnalyticsHeader
      }

      return newFilters;
    });

    updateUrlParams(key, value);
    captureEvent("ANL_FilterChange", {
      key,
      value,
      tab: activeTab,
    });
  };

  const updateUrlParams = (key, value) => {
    const current = Object.fromEntries(searchParams.entries());

    let urlKey = null;
    let urlValue = value;

    switch (key) {
      case "dateRange":
        urlKey = "time_range";
        urlValue = reverseDateRangeMap[value] || value;

        if (value !== "custom") {
          const newParams = { ...current };
          delete newParams.start_date;
          delete newParams.end_date;
          setSearchParams({
            ...newParams,
            [urlKey]: urlValue,
          });
          return;
        }
        break;
      case "customStartDate":
        if (filters.dateRange === "custom") {
          const newParams = {
            ...current,
            time_range: "custom",
            start_date: value,
          };
          if (filters.customEndDate) {
            newParams.end_date = filters.customEndDate;
          }
          setSearchParams(newParams);
        }
        return;
      case "customEndDate":
        if (filters.dateRange === "custom") {
          const newParams = {
            ...current,
            time_range: "custom",
            end_date: value,
          };
          if (filters.customStartDate) {
            newParams.start_date = filters.customStartDate;
          }
          setSearchParams(newParams);
        }
        return;
      case "callDirection":
        if (activeTab === "activity") {
          urlKey = "call_direction";
          urlValue = reverseCallDirectionMap[value] || value;
        } else {
          return;
        }
        break;

      case "userTeamSelector":
      case "viewToggle":
        return;
    }

    if (urlKey) {
      setSearchParams({
        ...current,
        [urlKey]: urlValue,
      });
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    captureEvent("ANL_TabChange", {
      tab,
    });

    const current = Object.fromEntries(searchParams.entries());
    const newParams = { ...current };

    if (tab !== "activity") {
      if (newParams.call_direction) {
        delete newParams.call_direction;
      }
    } else {
      if (!newParams.call_direction) {
        newParams.call_direction = reverseCallDirectionMap[filters.callDirection] || "outgoing";
      }
    }

    if (tab !== "best-times") {
      if (newParams.country) {
        delete newParams.country;
      }
      if (newParams.timezone) {
        delete newParams.timezone;
      }
    }

    setSearchParams(newParams);
  };

  //loading state
  if (isCheckingAccess) {
    return (
      <div className="-mx-8">
        <div className="mb-3 pl-8">
          <Text size="lg" className="text-[#101828] font-semibold">
            Activity
          </Text>
          <Text size="p" className="mt-1 text-text-500">
            Insights into call volume, quality, and conversions.
          </Text>
        </div>

        <div className="w-full">
          <div className="border-b border-gray-200 dark:border-[#22262F] pl-8">
            <nav className="flex space-x-4">
              <Skeleton className="h-10 w-20" />
              <Skeleton className="h-10 w-24" />
              <Skeleton className="h-10 w-20" />
            </nav>
          </div>

          <div className="px-8 py-4 border-b border-gray-200 dark:border-[#22262F]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-4">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-10 w-28" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-36" />
                <Skeleton className="h-10 w-32" />
              </div>
            </div>
          </div>

          <div className="mt-4 px-8">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                {[1, 2, 3, 4].map((index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-[#181D27] border border-gray-200 dark:border-[#22262F] rounded-lg p-5"
                  >
                    <div className="flex items-start gap-4">
                      <Skeleton className="w-10 h-10 rounded-lg shrink-0" />
                      <div className="flex-1">
                        <Skeleton className="h-4 w-24 mb-2" />
                        <Skeleton className="h-8 w-16" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white dark:bg-[#181D27] border border-gray-200 dark:border-[#22262F] rounded-lg p-4 sm:p-6">
                <div className="mb-4">
                  <Skeleton className="h-6 w-48 mb-2" />
                </div>
                <div className="w-full overflow-x-auto">
                  <div className="min-w-max">
                    <div className="flex mb-2">
                      <div className="w-12 sm:w-16 flex-shrink-0"></div>
                      <div
                        className="grid gap-0.5 w-full"
                        style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}
                      >
                        {Array.from({ length: 24 }).map((_, index) => (
                          <div key={index} className="text-center">
                            <Skeleton className="h-3 w-6 mx-auto" />
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-1">
                      {Array.from({ length: 7 }).map((_, dayIndex) => (
                        <div key={dayIndex} className="flex items-center">
                          <div className="w-12 sm:w-16 flex-shrink-0 pr-2">
                            <Skeleton className="h-4 w-8" />
                          </div>
                          <div
                            className="grid gap-0.5 w-full"
                            style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}
                          >
                            {Array.from({ length: 24 }).map((_, hourIndex) => (
                              <Skeleton key={hourIndex} className="aspect-square rounded-sm" />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-[#181D27] border border-gray-200 dark:border-[#22262F] rounded-lg p-6">
                <Skeleton className="h-6 w-40 mb-4" />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <Skeleton className="h-64 w-full rounded" />
                  </div>
                  <div className="space-y-4">
                    {[1, 2, 3, 4].map((index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Skeleton className="w-3 h-3 rounded-full" />
                          <Skeleton className="h-4 w-20" />
                        </div>
                        <Skeleton className="h-4 w-12" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (hasAccessDenied) {
    return <CallAnalyticsAccessDenied />;
  }

  return (
    <div className="-mx-8">
      <div className="mb-3 pl-8">
        <Text size="lg" className="text-[#101828] font-semibold">
          Activity
        </Text>
        <Text size="p" className="mt-1 text-text-500">
          Insights into call volume, quality, and conversions.
        </Text>
      </div>

      <div className="w-full">
        <div className="border-b border-gray-200 pl-8">
          <nav className="flex space-x-4">
            <button
              onClick={() => handleTabChange("activity")}
              className={`py-3 border-b-2 font-bold text-sm transition-colors ${
                activeTab === "activity"
                  ? "border-brand-600 text-[#6941C6] dark:text-[#CECFD2] font-semibold"
                  : "border-transparent text-[#717680] dark:text-[#94979C] hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => handleTabChange("best-times")}
              className={`py-3 border-b-2 font-bold text-sm transition-colors ${
                activeTab === "best-times"
                  ? "border-brand-600 text-[#6941C6] dark:text-[#CECFD2] font-semibold"
                  : "border-transparent text-[#717680] dark:text-[#94979C] hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Best Times
            </button>
            <button
              onClick={() => handleTabChange("numbers")}
              className={`py-3 border-b-2 font-bold text-sm transition-colors ${
                activeTab === "numbers"
                  ? "border-brand-600 text-[#6941C6] dark:text-[#CECFD2] font-semibold"
                  : "border-transparent text-[#717680] dark:text-[#94979C] hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              Numbers
            </button>
          </nav>
        </div>

        <div>
          <AnalyticsHeader
            filters={filters}
            onFilterChange={handleFilterChange}
            activeTab={activeTab}
            userAccessData={paginatedUserData}
            isLoadingUsers={isCheckingAccess}
            onLoadMoreUsers={handleLoadMoreUsers}
          />

          <div className="mt-4 px-8">
            {activeTab === "activity" && <ActivityTab filters={filters} hasAccess={!hasAccessDenied} />}
            {activeTab === "best-times" && <BestTimesTab filters={filters} hasAccess={!hasAccessDenied} />}
            {activeTab === "numbers" && <NumbersTab filters={filters} hasAccess={!hasAccessDenied} />}
          </div>
        </div>
      </div>
    </div>
  );
}
